import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { api, API, fmtMoney, formatApiErrorDetail } from "@/lib/api";
import { PayBadge } from "@/components/PayBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { DollarSign, Download, Wallet, Search, Settings, Plus, Trash2, FileText, RefreshCw, RotateCcw } from "lucide-react";

const CURRENCIES = ["USD", "CAD", "EUR", "GBP", "AUD"];

function periodOptions() {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 6; i++) {
    const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
    const value = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
    out.push({ value, label: dt.toLocaleString(undefined, { month: "long", year: "numeric" }) });
  }
  return out;
}

function Stat({ label, value, tone, testid, onClick }) {
  return (
    <button type="button" onClick={onClick} data-testid={`${testid}-card`}
      className="rounded-xl border border-slate-200 bg-white p-5 text-left transition hover:border-indigo-400 hover:shadow-sm">
      <p className="text-sm text-slate-500">{label}</p>
      <p data-testid={testid} className={`font-display text-2xl font-extrabold ${tone}`}>{value}</p>
      <p className="mt-1 text-xs font-semibold text-indigo-600">View records ›</p>
    </button>
  );
}

export default function AdminBilling() {
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [comps, setComps] = useState([]);
  const [tab, setTab] = useState("invoices");
  const [q, setQ] = useState("");
  const [payFilter, setPayFilter] = useState("all");
  const [payDialog, setPayDialog] = useState(null); // invoice being paid
  const [payAmount, setPayAmount] = useState("");
  const [payRef, setPayRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState({});
  const [servers, setServers] = useState([]);
  const [adjInv, setAdjInv] = useState(null); // invoice being adjusted
  const [expDesc, setExpDesc] = useState("");
  const [expAmt, setExpAmt] = useState("");
  const [discDesc, setDiscDesc] = useState("");
  const [discAmt, setDiscAmt] = useState("");
  const [stmtServer, setStmtServer] = useState("");
  const [stmtPeriod, setStmtPeriod] = useState(periodOptions()[0].value);
  const [drill, setDrill] = useState(null);
  const [bd, setBd] = useState(null);
  const [syncing, setSyncing] = useState(null);

  const syncStripe = async (inv) => {
    setSyncing(inv.id);
    try {
      const { data } = await api.post("/admin/stripe/reconcile", { invoice_id: inv.id });
      const r = (data.results || [])[0] || {};
      if (r.action === "reconciled_paid") toast.success(`Invoice ${inv.invoice_number} reconciled — marked Paid.`);
      else if (r.action === "already_paid" || r.action === "already_recorded") toast.message("Already reconciled.");
      else if (r.action === "not_paid_in_stripe" || r.action === "not_paid") toast.message("Stripe shows this invoice is not paid yet.");
      else toast.message(r.detail ? `Sync: ${r.detail}` : "Sync complete.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Could not sync with Stripe.");
    } finally { setSyncing(null); }
  };

  const load = () => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/invoices").then(({ data }) => setInvoices(data)).catch(() => {});
    api.get("/compensations").then(({ data }) => setComps(data)).catch(() => {});
    api.get("/admin/billing/breakdown").then(({ data }) => setBd(data)).catch(() => {});
    api.get("/admin/billing/settings").then(({ data }) => { setSettings(data); setSettingsForm(data); }).catch(() => {});
    api.get("/servers").then(({ data }) => setServers(data)).catch(() => {});
  };
  useEffect(load, []);

  const cur = invoices[0]?.currency || comps[0]?.currency || "USD";

  const filteredInvoices = useMemo(() => invoices.filter((i) => {
    if (payFilter !== "all" && i.payment_status !== payFilter) return false;
    if (q && !`${i.invoice_number} ${i.job_number} ${i.client_name} ${i.firm_name}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [invoices, payFilter, q]);

  const filteredComps = useMemo(() => comps.filter((c) => {
    if (payFilter !== "all" && c.payment_status !== payFilter) return false;
    if (q && !`${c.job_number} ${c.server_name || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [comps, payFilter, q]);

  const openPay = (inv) => { setPayDialog(inv); setPayAmount(String(inv.balance_due || 0)); setPayRef(""); };

  const recordPayment = async () => {
    if (!(Number(payAmount) > 0)) { toast.error("Enter a payment amount greater than 0"); return; }
    setBusy(true);
    try {
      await api.post(`/invoices/${payDialog.id}/payment`, { amount: Number(payAmount), reference: payRef });
      toast.success("Payment recorded");
      setPayDialog(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const [stripePay, setStripePay] = useState(null); // comp being paid via Stripe
  const [manualPay, setManualPay] = useState(null); // comp being recorded paid outside Stripe
  const [manualNote, setManualNote] = useState("");
  const [manualRef, setManualRef] = useState("");
  const [resetComp, setResetComp] = useState(null); // legacy paid-without-transfer row being reset
  const [resetNote, setResetNote] = useState("");

  const doStripePay = async () => {
    setBusy(true);
    try {
      await api.post(`/compensations/${stripePay.id}/pay`, {});
      toast.success("Stripe transfer created — payout marked paid");
      setStripePay(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doManualPay = async () => {
    if (!manualNote.trim()) { toast.error("A note is required."); return; }
    setBusy(true);
    try {
      await api.post(`/compensations/${manualPay.id}/pay-manual`, { note: manualNote.trim(), reference: manualRef.trim() });
      toast.success("Recorded as paid outside Stripe");
      setManualPay(null); setManualNote(""); setManualRef(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doReset = async () => {
    if (!resetNote.trim()) { toast.error("A note is required."); return; }
    setBusy(true);
    try {
      await api.post(`/compensations/${resetComp.id}/reset-unpaid`, { note: resetNote.trim() });
      toast.success("Payout reset to unpaid");
      setResetComp(null); setResetNote(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      const payload = {
        currency: settingsForm.currency,
        default_service_fee: Number(settingsForm.default_service_fee),
        default_server_payout: Number(settingsForm.default_server_payout),
        default_print_cost_per_page: Number(settingsForm.default_print_cost_per_page),
      };
      const { data } = await api.put("/admin/billing/settings", payload);
      setSettings(data); setSettingsForm(data);
      toast.success("Billing settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const refreshInvoice = async (id) => {
    const { data } = await api.get(`/invoices/${id}`);
    setAdjInv(data);
    setInvoices((prev) => prev.map((x) => (x.id === id ? data : x)));
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  };

  const adjust = async (payload) => {
    try {
      const { data } = await api.post(`/invoices/${adjInv.id}/adjustments`, payload);
      setAdjInv(data);
      setInvoices((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
      return true;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return false; }
  };

  const addExpenseLine = async () => {
    if (!expDesc || !(Number(expAmt) > 0)) { toast.error("Enter a description and amount greater than 0"); return; }
    const ok = await adjust({ action: "add_expense", description: expDesc, unit_amount: Number(expAmt), quantity: 1, dedupe_key: (crypto.randomUUID?.() || String(Math.random())) });
    if (ok) { setExpDesc(""); setExpAmt(""); toast.success("Expense added"); }
  };
  const addDiscountLine = async () => {
    if (!discDesc || !(Number(discAmt) > 0)) { toast.error("Enter a description and amount"); return; }
    const ok = await adjust({ action: "add_discount", description: discDesc, unit_amount: Number(discAmt), quantity: 1, dedupe_key: (crypto.randomUUID?.() || String(Math.random())) });
    if (ok) { setDiscDesc(""); setDiscAmt(""); toast.success("Discount added"); }
  };
  const removeLine = async (lineId) => {
    const ok = await adjust({ action: "remove_line", line_id: lineId });
    if (ok) toast.success("Line removed");
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Billing &amp; finance</h2>
        <p className="text-slate-500">Invoices, payments, server compensation, expenses and revenue.</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <Stat label="Billed" value={fmtMoney(cur, stats.billed)} tone="text-slate-900" testid="admin-billed" onClick={() => setDrill("billed")} />
          <Stat label="Collected" value={fmtMoney(cur, stats.collected)} tone="text-emerald-600" testid="admin-collected" onClick={() => setDrill("collected")} />
          <Stat label="Outstanding" value={fmtMoney(cur, stats.outstanding)} tone="text-amber-600" testid="admin-outstanding" onClick={() => setDrill("outstanding")} />
          <Stat label="Server payouts" value={fmtMoney(cur, stats.server_payouts)} tone="text-indigo-600" testid="admin-payouts" onClick={() => setDrill("server_payouts")} />
          <Stat label="ARPS revenue" value={fmtMoney(cur, stats.revenue)} tone="text-slate-900" testid="admin-revenue" onClick={() => setDrill("revenue")} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
          <button data-testid="tab-invoices" onClick={() => setTab("invoices")} className={`rounded-md px-4 py-1.5 text-sm font-semibold transition ${tab === "invoices" ? "bg-slate-900 text-white" : "text-slate-600"}`}>Invoices</button>
          <button data-testid="tab-compensations" onClick={() => setTab("comps")} className={`rounded-md px-4 py-1.5 text-sm font-semibold transition ${tab === "comps" ? "bg-slate-900 text-white" : "text-slate-600"}`}>Server compensation</button>
          <button data-testid="tab-settings" onClick={() => setTab("settings")} className={`rounded-md px-4 py-1.5 text-sm font-semibold transition ${tab === "settings" ? "bg-slate-900 text-white" : "text-slate-600"}`}>Fee settings</button>
        </div>
        {tab !== "settings" && (<>
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="billing-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9" />
        </div>
        <Select value={payFilter} onValueChange={setPayFilter}>
          <SelectTrigger data-testid="billing-status-filter" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any status</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
          </SelectContent>
        </Select>
        </>)}
      </div>

      {tab === "invoices" ? (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-5 py-3">Invoice #</th><th className="px-5 py-3">Client</th>
                <th className="px-5 py-3">Job #</th><th className="px-5 py-3">Total</th>
                <th className="px-5 py-3">Balance</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredInvoices.map((i) => (
                <tr key={i.id} data-testid={`admin-invoice-row-${i.invoice_number}`} className="hover:bg-slate-50">
                  <td className="px-5 py-3 font-mono-arps font-semibold text-slate-900">{i.invoice_number}</td>
                  <td className="px-5 py-3 text-slate-600">{i.client_name}<span className="block text-xs text-slate-400">{i.firm_name}</span></td>
                  <td className="px-5 py-3 text-slate-500">#{i.job_number}</td>
                  <td className="px-5 py-3 font-semibold text-slate-900">{fmtMoney(i.currency, i.total)}</td>
                  <td className="px-5 py-3 text-slate-600">{fmtMoney(i.currency, i.balance_due)}</td>
                  <td className="px-5 py-3"><PayBadge status={i.payment_status} /></td>
                  <td className="px-5 py-3">
                    <div className="flex gap-2">
                      <a href={`${API}/invoices/${i.id}/receipt`} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="outline"><Download className="h-3.5 w-3.5" /></Button>
                      </a>
                      {(i.amount_paid || 0) === 0 && i.status !== "void" && (
                        <Button data-testid={`adjust-invoice-${i.invoice_number}`} size="sm" variant="outline" onClick={() => setAdjInv(i)}>
                          <Settings className="mr-1 h-3.5 w-3.5" /> Adjust
                        </Button>
                      )}
                      {i.payment_status !== "paid" && (
                        <Button data-testid={`record-payment-${i.invoice_number}`} size="sm" onClick={() => openPay(i)} className="bg-emerald-600 text-white hover:bg-emerald-700">
                          <DollarSign className="mr-1 h-3.5 w-3.5" /> Record
                        </Button>
                      )}
                      {i.payment_status !== "paid" && i.stripe_session_id && (
                        <Button data-testid={`sync-stripe-${i.invoice_number}`} size="sm" variant="outline" disabled={syncing === i.id} onClick={() => syncStripe(i)}>
                          <RefreshCw className={`mr-1 h-3.5 w-3.5 ${syncing === i.id ? "animate-spin" : ""}`} /> Sync with Stripe
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredInvoices.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-400">No invoices.</td></tr>}
            </tbody>
          </table>
        </div>
      ) : tab === "comps" ? (
        <>
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4">
          <div>
            <Label className="text-xs text-slate-500">Payout statement — server</Label>
            <Select value={stmtServer} onValueChange={setStmtServer}>
              <SelectTrigger data-testid="statement-server-select" className="w-56"><SelectValue placeholder="Select server" /></SelectTrigger>
              <SelectContent>
                {servers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Period</Label>
            <Select value={stmtPeriod} onValueChange={setStmtPeriod}>
              <SelectTrigger data-testid="statement-period-select" className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                {periodOptions().map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <a data-testid="download-statement-link" className={stmtServer ? "" : "pointer-events-none opacity-40"}
             href={stmtServer ? `${API}/compensations/statement.pdf?server_id=${stmtServer}&period=${stmtPeriod}` : "#"}
             target="_blank" rel="noreferrer">
            <Button size="sm" variant="outline"><FileText className="mr-1.5 h-3.5 w-3.5" /> Download statement</Button>
          </a>
        </div>
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-5 py-3">Job #</th><th className="px-5 py-3">Server</th>
                <th className="px-5 py-3">Gross</th><th className="px-5 py-3">Deductions</th>
                <th className="px-5 py-3">Net payout</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredComps.map((c) => (
                <tr key={c.id} data-testid={`admin-comp-row-${c.job_number}`} className="hover:bg-slate-50">
                  <td className="px-5 py-3 font-mono-arps font-semibold text-slate-900">#{c.job_number}</td>
                  <td className="px-5 py-3 text-slate-600">{c.server_name || "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{fmtMoney(c.currency, c.gross_payout)}</td>
                  <td className="px-5 py-3 text-red-600">- {fmtMoney(c.currency, c.deductions)}</td>
                  <td className="px-5 py-3 font-semibold text-slate-900">{fmtMoney(c.currency, c.net_payout)}</td>
                  <td className="px-5 py-3"><PayBadge status={c.payment_status} /></td>
                  <td className="px-5 py-3">
                    {c.payment_status !== "paid" && (
                      <div className="flex flex-wrap gap-2">
                        <Button data-testid={`pay-comp-${c.job_number}`} size="sm" onClick={() => setStripePay(c)} className="bg-slate-900 text-white hover:bg-slate-800">
                          <Wallet className="mr-1 h-3.5 w-3.5" /> Pay via Stripe
                        </Button>
                        <Button data-testid={`pay-comp-manual-${c.job_number}`} size="sm" variant="outline" onClick={() => { setManualNote(""); setManualRef(""); setManualPay(c); }} className="border-slate-300 text-slate-600 hover:bg-slate-50">
                          Paid outside Stripe
                        </Button>
                      </div>
                    )}
                    {c.payment_status === "paid" && !String(c.stripe_transfer_id || "").startsWith("tr_") && (
                      <Button data-testid={`reset-comp-${c.job_number}`} size="sm" variant="outline" onClick={() => { setResetNote(""); setResetComp(c); }} className="border-red-200 text-red-600 hover:bg-red-50">
                        <RotateCcw className="mr-1 h-3.5 w-3.5" /> Reset to unpaid
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {filteredComps.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-400">No compensation records.</td></tr>}
            </tbody>
          </table>
        </div>
        </>
      ) : (
        <div data-testid="fee-settings-panel" className="max-w-2xl rounded-xl border border-slate-200 bg-white p-6">
          <div className="mb-3 flex items-center gap-2">
            <Settings className="h-5 w-5 text-slate-400" />
            <h3 className="font-display text-lg font-bold text-slate-900">Default fees</h3>
          </div>
          <p className="mb-4 text-sm text-slate-500">New billing records use these values at creation time. Existing invoices and payouts never change.</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-sm text-slate-600">Currency</Label>
              <Select value={settingsForm.currency || "USD"} onValueChange={(v) => setSettingsForm((f) => ({ ...f, currency: v }))}>
                <SelectTrigger data-testid="settings-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-slate-600">Default service fee</Label>
              <Input data-testid="settings-service-fee" type="number" min={0} value={settingsForm.default_service_fee ?? ""} onChange={(e) => setSettingsForm((f) => ({ ...f, default_service_fee: e.target.value }))} />
            </div>
            <div>
              <Label className="text-sm text-slate-600">Default server payout</Label>
              <Input data-testid="settings-server-payout" type="number" min={0} value={settingsForm.default_server_payout ?? ""} onChange={(e) => setSettingsForm((f) => ({ ...f, default_server_payout: e.target.value }))} />
            </div>
            <div>
              <Label className="text-sm text-slate-600">Print cost per page</Label>
              <Input data-testid="settings-print-cost" type="number" min={0} value={settingsForm.default_print_cost_per_page ?? ""} onChange={(e) => setSettingsForm((f) => ({ ...f, default_print_cost_per_page: e.target.value }))} />
            </div>
          </div>
          <Button data-testid="save-settings-button" onClick={saveSettings} disabled={busy} className="mt-5 bg-slate-900 text-white hover:bg-slate-800">{busy ? "Saving…" : "Save settings"}</Button>
          {settings?.audit_log?.length > 0 && (
            <div className="mt-6 border-t border-slate-100 pt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Recent changes</p>
              <ul data-testid="settings-audit-log" className="space-y-1.5 text-xs text-slate-500">
                {settings.audit_log.slice(-6).reverse().map((a, idx) => (
                  <li key={idx}>{new Date(a.at).toLocaleString()} — {a.by} changed <b>{a.setting}</b> {String(a.previous)} → {String(a.new)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <Dialog open={!!payDialog} onOpenChange={(o) => !o && setPayDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record payment {payDialog ? `— ${payDialog.invoice_number}` : ""}</DialogTitle>
            <DialogDescription>Manual ledger entry only. No live payment processor is called in this build.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-slate-600">Amount</label>
              <Input data-testid="payment-amount-input" type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
            </div>
            <div>
              <label className="text-sm text-slate-600">Reference (optional)</label>
              <Input data-testid="payment-ref-input" value={payRef} onChange={(e) => setPayRef(e.target.value)} placeholder="e.g. CHK-1042" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="confirm-payment-button" onClick={recordPayment} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700">{busy ? "Saving…" : "Record payment"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!stripePay} onOpenChange={(o) => !o && setStripePay(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Pay via Stripe {stripePay ? `— #${stripePay.job_number}` : ""}</DialogTitle>
            <DialogDescription>This creates a real Stripe Connect transfer to the provider's connected account, drawn from the original client charge. The payout is marked paid only after Stripe returns a transfer id.</DialogDescription>
          </DialogHeader>
          {stripePay && <p className="text-sm text-slate-600">Net payout <strong>{fmtMoney(stripePay.currency, stripePay.net_payout)}</strong> to {stripePay.server_name || "the provider"}.</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStripePay(null)} disabled={busy}>Cancel</Button>
            <Button data-testid="confirm-stripe-pay" onClick={doStripePay} disabled={busy} className="bg-slate-900 text-white hover:bg-slate-800">{busy ? "Processing…" : "Create Stripe transfer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!manualPay} onOpenChange={(o) => !o && setManualPay(null)}>        <DialogContent>
          <DialogHeader><DialogTitle>Record payment made outside Stripe {manualPay ? `— #${manualPay.job_number}` : ""}</DialogTitle>
            <DialogDescription>Use this ONLY if the provider was already paid another way (check, Zelle, etc.). No Stripe transfer is created. A note is required.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm text-slate-600">Note (required)</Label>
              <Input data-testid="manual-pay-note" value={manualNote} onChange={(e) => setManualNote(e.target.value)} placeholder="e.g. Paid by check #1042 on Oct 9" />
            </div>
            <div>
              <Label className="text-sm text-slate-600">Reference (optional)</Label>
              <Input data-testid="manual-pay-ref" value={manualRef} onChange={(e) => setManualRef(e.target.value)} placeholder="External reference" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualPay(null)} disabled={busy}>Cancel</Button>
            <Button data-testid="confirm-manual-pay" onClick={doManualPay} disabled={busy || !manualNote.trim()} className="bg-amber-600 text-white hover:bg-amber-700">{busy ? "Saving…" : "Record as paid outside Stripe"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetComp} onOpenChange={(o) => !o && setResetComp(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset payout to unpaid {resetComp ? `— #${resetComp.job_number}` : ""}</DialogTitle>
            <DialogDescription>One-time correction for a payout that was marked paid WITHOUT a real Stripe transfer. This sets it back to unpaid so cash-out / "Pay via Stripe" can run. It is refused if a real Stripe transfer exists. A note is required.</DialogDescription>
          </DialogHeader>
          {resetComp && <p className="text-sm text-slate-600">Net payout <strong>{fmtMoney(resetComp.currency, resetComp.net_payout)}</strong> to {resetComp.server_name || "the provider"}.</p>}
          <div>
            <Label className="text-sm text-slate-600">Note (required)</Label>
            <Input data-testid="reset-note" value={resetNote} onChange={(e) => setResetNote(e.target.value)} placeholder="e.g. Marked paid in error — no Stripe transfer was created" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetComp(null)} disabled={busy}>Cancel</Button>
            <Button data-testid="confirm-reset" onClick={doReset} disabled={busy || !resetNote.trim()} className="bg-red-600 text-white hover:bg-red-700">{busy ? "Resetting…" : "Reset to unpaid"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      <Dialog open={!!adjInv} onOpenChange={(o) => !o && setAdjInv(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Adjust invoice {adjInv ? `— ${adjInv.invoice_number}` : ""}</DialogTitle>
            <DialogDescription>Add expenses or discounts, or remove lines before payment. Every change is audit-logged.</DialogDescription>
          </DialogHeader>
          {adjInv && (
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-200">
                {(adjInv.line_items || []).map((li) => (
                  <div key={li.id} data-testid={`adj-line-${li.id}`} className="flex items-center justify-between border-b border-slate-100 px-3 py-2 text-sm last:border-0">
                    <div>
                      <span className="font-medium text-slate-700">{li.description}</span>
                      <span className="ml-2 text-xs uppercase text-slate-400">{li.type}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={li.type === "discount" ? "text-red-600" : "text-slate-800"}>{li.type === "discount" ? "- " : ""}{fmtMoney(adjInv.currency, li.amount)}</span>
                      {li.type !== "service_fee" && (
                        <button data-testid={`remove-line-${li.id}`} onClick={() => removeLine(li.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Total</span>
                <span data-testid="adj-total" className="font-display text-lg font-bold text-slate-900">{fmtMoney(adjInv.currency, adjInv.total)}</span>
              </div>
              <div className="grid gap-2 rounded-lg bg-slate-50 p-3 sm:grid-cols-[1fr_7rem_auto]">
                <Input data-testid="adj-expense-desc" placeholder="Expense description" value={expDesc} onChange={(e) => setExpDesc(e.target.value)} />
                <Input data-testid="adj-expense-amount" type="number" min={0} placeholder="Amount" value={expAmt} onChange={(e) => setExpAmt(e.target.value)} />
                <Button data-testid="add-expense-line" size="sm" onClick={addExpenseLine} className="bg-slate-900 text-white hover:bg-slate-800"><Plus className="mr-1 h-3.5 w-3.5" /> Expense</Button>
              </div>
              <div className="grid gap-2 rounded-lg bg-slate-50 p-3 sm:grid-cols-[1fr_7rem_auto]">
                <Input data-testid="adj-discount-desc" placeholder="Discount description" value={discDesc} onChange={(e) => setDiscDesc(e.target.value)} />
                <Input data-testid="adj-discount-amount" type="number" min={0} placeholder="Amount" value={discAmt} onChange={(e) => setDiscAmt(e.target.value)} />
                <Button data-testid="add-discount-line" size="sm" variant="outline" onClick={addDiscountLine}><Plus className="mr-1 h-3.5 w-3.5" /> Discount</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto">
          {drill && bd && (() => {
            const cfg = {
              billed: { title: "Billed — every invoice", total: bd.totals.billed, rows: bd.billed },
              collected: { title: "Collected — every payment", total: bd.totals.collected, rows: bd.collected },
              outstanding: { title: "Outstanding — receivables", total: bd.totals.outstanding, rows: bd.outstanding },
              server_payouts: { title: "Server payouts", total: bd.totals.server_payouts, rows: bd.server_payouts },
              revenue: { title: "ARPS revenue", total: bd.totals.revenue, rows: [] },
            }[drill];
            const sumField = { billed: "contribution", collected: "contribution", outstanding: "balance_outstanding", server_payouts: "contribution" }[drill];
            const rowsSum = drill === "revenue"
              ? bd.revenue.total
              : (cfg.rows.reduce((s, r) => s + Number(r[sumField] || 0), 0));
            const reconciles = Math.abs(Number(cfg.total) - Number(rowsSum)) < 0.01;
            return (
              <>
                <DialogHeader>
                  <DialogTitle data-testid="drill-title">{cfg.title}</DialogTitle>
                  <DialogDescription>
                    Card total <b>{fmtMoney(cur, cfg.total)}</b> = sum of records below.
                    <span data-testid="drill-reconcile" className={`ml-2 rounded px-2 py-0.5 text-xs font-semibold ${reconciles ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                      {reconciles ? "Reconciled ✓" : `Mismatch: records = ${fmtMoney(cur, rowsSum)}`}
                    </span>
                  </DialogDescription>
                </DialogHeader>

                {drill === "revenue" ? (
                  <div className="space-y-4 text-sm" data-testid="drill-revenue">
                    <p className="text-xs text-slate-500">ARPS revenue = collected client payments − server payouts already paid.</p>
                    <div>
                      <p className="mb-1 font-semibold text-emerald-700">+ Client payments collected ({bd.revenue.positive.length})</p>
                      <table className="w-full"><thead className="text-left text-xs uppercase text-slate-400"><tr><th className="py-1">Invoice</th><th>Job</th><th>Client</th><th className="text-right">Client payment</th></tr></thead>
                        <tbody className="divide-y divide-slate-100">{bd.revenue.positive.map((r, i) => (<tr key={i}><td className="py-1 font-mono-arps">{r.invoice_number}</td><td>#{r.job_number}</td><td>{r.client_name}</td><td className="text-right text-emerald-700">+ {fmtMoney(cur, r.client_payment)}</td></tr>))}</tbody></table>
                    </div>
                    <div>
                      <p className="mb-1 font-semibold text-red-700">− Server payouts paid ({bd.revenue.negative.length})</p>
                      <table className="w-full"><thead className="text-left text-xs uppercase text-slate-400"><tr><th className="py-1">Job</th><th>Server</th><th className="text-right">Server compensation</th></tr></thead>
                        <tbody className="divide-y divide-slate-100">{bd.revenue.negative.map((r, i) => (<tr key={i}><td className="py-1">#{r.job_number}</td><td>{r.server_name}</td><td className="text-right text-red-600">− {fmtMoney(cur, r.server_compensation)}</td></tr>))}</tbody></table>
                    </div>
                    <div className="flex justify-between border-t pt-2 font-display text-lg font-bold"><span>Net ARPS revenue</span><span data-testid="drill-total">{fmtMoney(cur, bd.revenue.total)}</span></div>
                  </div>
                ) : (
                  <div className="overflow-x-auto" data-testid={`drill-${drill}`}>
                    <table className="w-full text-sm">
                      <thead className="border-b bg-slate-50 text-left text-xs uppercase text-slate-500"><tr>
                        {drill === "server_payouts" ? (<>
                          <th className="px-3 py-2">Server</th><th className="px-3 py-2">Job</th><th className="px-3 py-2 text-right">Gross</th><th className="px-3 py-2 text-right">Fees</th><th className="px-3 py-2 text-right">Net</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Method</th><th className="px-3 py-2">Reference</th><th className="px-3 py-2">Initiated</th><th className="px-3 py-2">Processed</th>
                        </>) : drill === "outstanding" ? (<>
                          <th className="px-3 py-2">Invoice</th><th className="px-3 py-2">Job</th><th className="px-3 py-2">Client</th><th className="px-3 py-2">Type</th><th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2 text-right">Paid</th><th className="px-3 py-2 text-right">Balance</th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Days</th><th className="px-3 py-2">Terms</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Authorized</th><th className="px-3 py-2">Reason</th><th className="px-3 py-2"></th>
                        </>) : drill === "collected" ? (<>
                          <th className="px-3 py-2">Invoice</th><th className="px-3 py-2">Job</th><th className="px-3 py-2">Client</th><th className="px-3 py-2 text-right">Collected</th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Method</th><th className="px-3 py-2">Reference</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th>
                        </>) : (<>
                          <th className="px-3 py-2">Invoice</th><th className="px-3 py-2">Job</th><th className="px-3 py-2">Client</th><th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Date</th><th className="px-3 py-2 text-right">Paid</th><th className="px-3 py-2 text-right">Balance</th><th className="px-3 py-2"></th>
                        </>)}
                      </tr></thead>
                      <tbody className="divide-y divide-slate-100">
                        {cfg.rows.map((r) => (
                          <tr key={r.id} data-testid={`drill-row-${r.invoice_number || r.job_number}`} className={r.is_test ? "bg-amber-50/60" : ""}>
                            {drill === "server_payouts" ? (<>
                              <td className="px-3 py-2">{r.server_name}</td><td className="px-3 py-2">#{r.job_number}</td><td className="px-3 py-2 text-right">{fmtMoney(cur, r.gross_compensation)}</td><td className="px-3 py-2 text-right text-red-600">{fmtMoney(cur, r.fees)}</td><td className="px-3 py-2 text-right font-semibold">{fmtMoney(cur, r.net_amount)}</td><td className="px-3 py-2"><PayBadge status={r.payout_status} /></td><td className="px-3 py-2">{r.payout_method}</td><td className="px-3 py-2 text-xs">{r.payout_reference || "—"}</td><td className="px-3 py-2 text-xs">{r.date_initiated ? new Date(r.date_initiated).toLocaleDateString() : "—"}</td><td className="px-3 py-2 text-xs">{r.date_processed ? new Date(r.date_processed).toLocaleDateString() : "—"}</td>
                            </>) : drill === "outstanding" ? (<>
                              <td className="px-3 py-2 font-mono-arps">{r.invoice_number}</td><td className="px-3 py-2">#{r.job_number}</td><td className="px-3 py-2">{r.client_name}</td><td className="px-3 py-2"><span className={`rounded px-1.5 py-0.5 text-xs ${r.client_type === "Invite-Only" ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600"}`}>{r.client_type}</span></td><td className="px-3 py-2 text-right">{fmtMoney(cur, r.invoice_amount)}</td><td className="px-3 py-2 text-right">{fmtMoney(cur, r.amount_paid)}</td><td className="px-3 py-2 text-right font-semibold text-amber-700">{fmtMoney(cur, r.balance_outstanding)}</td><td className="px-3 py-2 text-xs">{r.invoice_date ? new Date(r.invoice_date).toLocaleDateString() : "—"}</td><td className="px-3 py-2">{r.days_outstanding ?? "—"}</td><td className="px-3 py-2 text-xs">{r.payment_terms}</td><td className="px-3 py-2"><PayBadge status={r.payment_status} /></td><td className="px-3 py-2">{r.is_test ? <span className="rounded bg-amber-200 px-1.5 py-0.5 text-xs font-semibold text-amber-800">TEST DATA</span> : (r.authorized ? <span className="text-emerald-600">✓ Yes</span> : <span className="font-semibold text-red-600">✗ No</span>)}</td><td className="px-3 py-2 text-xs text-slate-500">{r.reason}</td><td className="px-3 py-2"><a href={`${API}/invoices/${r.id}/receipt`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" data-testid={`investigate-${r.invoice_number}`}>Investigate</Button></a></td>
                            </>) : drill === "collected" ? (<>
                              <td className="px-3 py-2 font-mono-arps">{r.invoice_number}</td><td className="px-3 py-2">#{r.job_number}</td><td className="px-3 py-2">{r.client_name}</td><td className="px-3 py-2 text-right font-semibold text-emerald-700">{fmtMoney(cur, r.amount_collected)}</td><td className="px-3 py-2 text-xs">{r.payment_date ? new Date(r.payment_date).toLocaleDateString() : "—"}</td><td className="px-3 py-2">{r.payment_method}</td><td className="px-3 py-2 text-xs">{r.payment_reference || "—"}</td><td className="px-3 py-2"><PayBadge status={r.payment_status} /></td><td className="px-3 py-2"><a href={`${API}/invoices/${r.id}/receipt`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline">Investigate</Button></a></td>
                            </>) : (<>
                              <td className="px-3 py-2 font-mono-arps">{r.invoice_number}</td><td className="px-3 py-2">#{r.job_number}</td><td className="px-3 py-2">{r.client_name}</td><td className="px-3 py-2 text-right font-semibold">{fmtMoney(cur, r.invoice_amount)}</td><td className="px-3 py-2"><PayBadge status={r.payment_status} /></td><td className="px-3 py-2 text-xs">{r.invoice_date ? new Date(r.invoice_date).toLocaleDateString() : "—"}</td><td className="px-3 py-2 text-right">{fmtMoney(cur, r.amount_paid)}</td><td className="px-3 py-2 text-right">{fmtMoney(cur, r.balance_due)}</td><td className="px-3 py-2"><a href={`${API}/invoices/${r.id}/receipt`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline">Investigate</Button></a></td>
                            </>)}
                          </tr>
                        ))}
                        {cfg.rows.length === 0 && <tr><td colSpan={14} className="px-3 py-10 text-center text-slate-400">No contributing records.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
